import { useEffect, useRef, useState } from 'react';

import { Edit2, ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AVATAR_FALLBACK_COLOR } from '@/lib/constants/colors';
import { cn } from '@/lib/utils';
import {
  imageCache,
  imageDownscaleInFlight,
  imageFailedUrls,
  imageFetchInFlight,
} from '@/modules/common/utils/image-cache';
import { downscaleImage, getAvatarDownscaleDimensions } from '@/modules/common/utils/image-utils';

/** Carries the HTTP status so the catch block can distinguish 4xx (permanent) from 5xx (transient). */
class ImageFetchError extends Error {
  constructor(public readonly status: number) {
    super(`Failed to fetch image: ${status}`);
  }
}

interface OpsiumAvatarProps {
  /**
   * Current image URL or File object
   */
  image?: string | File | null;
  /**
   * Background color for fallback
   */
  color?: string;
  /**
   * Name for fallback initials
   */
  name?: string;
  /**
   * Whether avatar can be edited
   */
  editable?: boolean;
  /**
   * Callback when image is selected
   */
  onImageChange?: (file: File) => void;
  /**
   * Callback when image is removed
   */
  onImageRemove?: () => void;
  /**
   * Loading state for upload
   */
  isUploading?: boolean;
  /**
   * Avatar size
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xs';
  /**
   * Shape of the avatar
   */
  shape?: 'circle' | 'square' | 'rounded';
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Alt text for the image
   */
  alt?: string;
  /**
   * Whether to downscale the image for better performance.
   * Set to true for user-uploaded photos from backend.
   * Set to false for icons and logos that need to stay crisp.
   * @default true
   */
  shouldDownscale?: boolean;
  /**
   * Whether to show a border around the avatar.
   * @default false
   */
  withBorder?: boolean;
}

export function EntityAvatar({
  image,
  color = AVATAR_FALLBACK_COLOR,
  name = '',
  editable = false,
  onImageChange,
  onImageRemove,
  isUploading = false,
  size = 'md',
  shape = 'circle',
  className,
  alt,
  shouldDownscale = true,
  withBorder = false,
}: OpsiumAvatarProps) {
  const { t } = useTranslation('common');
  const [isHovered, setIsHovered] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [downscaledImageUrl, setDownscaledImageUrl] = useState<string | null>(() => {
    // Initialize from cache synchronously to avoid flash of fallback initials on remount
    if (typeof image === 'string' && image.trim() !== '') {
      // Skip known-failed URLs — show fallback immediately
      if (imageFailedUrls.has(image)) return null;
      if (!shouldDownscale) return image;
      const cached = imageCache.get(image, size);
      if (cached) return cached;
    }
    return null;
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sizeClasses = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
  };

  const editIconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6',
  };

  const shapeClasses = {
    circle: 'rounded-full',
    square: 'rounded-none',
    rounded: 'rounded-lg',
  };

  // Get initials from name
  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImageChange) {
      onImageChange(file);
    }
    // Reset input to allow selecting same file again
    event.target.value = '';
  };

  // Handle click to open file dialog (used when no dropdown menu)
  const handleClick = () => {
    if (editable && !isUploading && !showDropdownMenu) {
      fileInputRef.current?.click();
    }
  };

  // Handle image load events
  const handleImageLoad = () => {
    setIsImageLoading(false);
    setImageLoadError(false);
  };

  const handleImageError = () => {
    setIsImageLoading(false);
    setImageLoadError(true);
  };

  const isClickable = editable && !isUploading;
  const showEditOverlay = isClickable && isHovered;
  const shouldShowInitials = !downscaledImageUrl || imageLoadError || isImageLoading;
  const hasLoadedImage = downscaledImageUrl && !imageLoadError && !isImageLoading;

  // Show dropdown menu when editable and there's an image that can be removed
  const hasImage = !!image && hasLoadedImage;
  const showDropdownMenu = editable && hasImage && !!onImageRemove;

  // Downscale both File objects and URL images for better quality rendering
  useEffect(() => {
    let cancelled = false;

    const processImage = async () => {
      if (!image) {
        if (!cancelled) {
          setDownscaledImageUrl(null);
          setIsImageLoading(false);
        }
        return;
      }

      // Skip invalid URLs (empty strings, whitespace, etc.)
      if (typeof image === 'string' && image.trim() === '') {
        if (!cancelled) {
          setDownscaledImageUrl(null);
          setIsImageLoading(false);
        }
        return;
      }

      // Skip URLs known to have failed (404, etc.) — show fallback without fetching
      if (typeof image === 'string' && imageFailedUrls.has(image)) {
        if (!cancelled) {
          setDownscaledImageUrl(null);
          setIsImageLoading(false);
        }
        return;
      }

      // If downscaling is disabled, use the image directly
      if (!shouldDownscale && typeof image === 'string') {
        if (!cancelled) {
          setDownscaledImageUrl(image);
          setIsImageLoading(false);
          // Reset the error latch — otherwise a spurious <img> error event from a
          // previous (interrupted) load keeps the avatar stuck on initials even
          // though this is a valid URL. Common when many same-URL avatars mount
          // and reorder together (e.g. one employee across every table row).
          setImageLoadError(false);
        }
        return;
      }

      // Check cache first (single lookup, no redundant has() + get())
      const cacheKey = typeof image === 'string' ? image : null;
      if (cacheKey) {
        const cached = imageCache.get(cacheKey, size);
        if (cached) {
          if (!cancelled) {
            setDownscaledImageUrl(cached);
            setIsImageLoading(false);
          }
          return;
        }
      }

      if (!cancelled) {
        setIsImageLoading(true);
        setImageLoadError(false);
      }

      try {
        const dimensions = getAvatarDownscaleDimensions(size);
        const downscaleKey = cacheKey ? `${cacheKey}_${size}` : null;

        const getBlobUrl = async (): Promise<string> => {
          if (image instanceof File) {
            return downscaleImage(image, dimensions.width, dimensions.height, 0.9);
          }

          if (typeof image === 'string') {
            // Reuse an existing in-flight downscale for the same URL+size
            if (downscaleKey && imageDownscaleInFlight.has(downscaleKey)) {
              return imageDownscaleInFlight.get(downscaleKey)!;
            }

            // Two-level dedup: fetch the original blob once per URL,
            // then downscale per size. Multiple avatar sizes sharing the same
            // source URL will reuse a single network request.
            // No abort signal — shared fetches must not be killed by a single unmount.
            const fetchBlob = (): Promise<Blob> => {
              if (imageFetchInFlight.has(image)) {
                return imageFetchInFlight.get(image)!;
              }
              const fetchPromise = fetch(image, { cache: 'default' })
                .then(response => {
                  if (!response.ok) throw new ImageFetchError(response.status);
                  return response.blob();
                })
                .finally(() => imageFetchInFlight.delete(image));

              imageFetchInFlight.set(image, fetchPromise);
              return fetchPromise;
            };

            const downscalePromise = fetchBlob()
              .then(blob => downscaleImage(blob, dimensions.width, dimensions.height, 0.9))
              .finally(() => {
                if (downscaleKey) imageDownscaleInFlight.delete(downscaleKey);
              });

            if (downscaleKey) imageDownscaleInFlight.set(downscaleKey, downscalePromise);
            return downscalePromise;
          }

          throw new Error('Invalid image type');
        };

        const blobUrl = await getBlobUrl();

        // Cache the result for URL-based images regardless of cancelled state.
        // The fetch+downscale already completed — caching it avoids repeating the work
        // for the next component that needs this URL+size.
        if (cacheKey) {
          imageCache.set(cacheKey, size, blobUrl);
        }

        if (!cancelled) {
          setDownscaledImageUrl(blobUrl);
          setIsImageLoading(false);
        }
      } catch (error) {
        // Only cache 4xx errors (image doesn't exist) — not 5xx (transient server errors)
        // and not aborts / network failures which may be temporary.
        const is4xx = error instanceof ImageFetchError && error.status >= 400 && error.status < 500;
        if (cacheKey && is4xx) {
          imageFailedUrls.mark(cacheKey);
        }
        if (!cancelled) {
          setImageLoadError(true);
          setIsImageLoading(false);
          setDownscaledImageUrl(null);
        }
      }
    };

    processImage();
    return () => {
      cancelled = true;
    };
  }, [image, size, shouldDownscale]);

  // Give every new source a fresh chance to load. `imageLoadError` is a sticky
  // latch set by the <img> onError handler; without this reset, a transient error
  // on a prior source (e.g. when many identical <img> mount/reorder at once) would
  // permanently pin the avatar to initials even after the URL resolves correctly.
  useEffect(() => {
    setImageLoadError(false);
  }, [downscaledImageUrl]);

  const avatarElement = (
    <Avatar
      className={cn(
        sizeClasses[size],
        shapeClasses[shape],
        isClickable && 'cursor-pointer',
        isUploading && 'opacity-75',
        withBorder && !hasLoadedImage && 'ring-1 ring-border',
        className,
        !hasLoadedImage && 'bg-muted'
      )}
      onMouseEnter={() => isClickable && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {/* Upload loading state */}
      {isUploading && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-background/80 z-10',
            shapeClasses[shape]
          )}
        >
          <Loader2 className={`${editIconSizes[size]} animate-spin text-foreground`} />
        </div>
      )}

      {/* Avatar image — uses plain img instead of Radix AvatarImage to avoid
         Radix's internal loading state reset which causes fallback flash on re-render */}
      {downscaledImageUrl && !imageLoadError && (
        <img
          src={downscaledImageUrl}
          alt={alt || `${name} avatar`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          className="aspect-square h-full w-full object-cover"
        />
      )}

      {/* Fallback with initials */}
      {shouldShowInitials && (
        <AvatarFallback
          className={cn(
            textSizeClasses[size],
            shapeClasses[shape],
            'font-semibold text-primary select-none'
          )}
          style={{ backgroundColor: color || 'hsl(var(--primary))' }}
        >
          {getInitials(name)}
        </AvatarFallback>
      )}

      {/* Edit overlay */}
      {showEditOverlay && (
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center bg-accent/70 z-20',
            shapeClasses[shape]
          )}
        >
          <Edit2 className={`${editIconSizes[size]} text-accent-foreground`} />
        </div>
      )}
    </Avatar>
  );

  return (
    <div className="relative flex-shrink-0">
      {showDropdownMenu ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{avatarElement}</DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
              <ImagePlus className="h-4 w-4" />
              {t('avatar.changePhoto')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onImageRemove}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
              {t('avatar.removePhoto')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        avatarElement
      )}

      {/* Hidden file input */}
      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      )}
    </div>
  );
}
